/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@mui/material';
import { Image } from '../../../../types/elements/Image';
import { ChoiceViewUseStyles } from './ChoiceViewStyles';
import { ELEMENTS_TYPE } from '../../../../utils/elementFactory';

interface Props {
	textOptions: string[] | undefined;
	imageOptions: Image[] | undefined;
	selectedOptions: string[] | undefined;
	type: ELEMENTS_TYPE;
}

enum OPTION_TYPE {
	TEXT,
	IMAGE,
}

export const MultipleChoiceView = observer((props: Props) => {
	const { textOptions, imageOptions, selectedOptions, type } = props;
	const { classes } = ChoiceViewUseStyles();

	const isChecked = (index: number, optionType: OPTION_TYPE): boolean => {
		if (type === ELEMENTS_TYPE.MODULE) {
			return false;
		} else if (!!selectedOptions && !!textOptions && !!imageOptions) {
			return optionType === OPTION_TYPE.TEXT
				? selectedOptions.includes(textOptions[index])
				: selectedOptions.includes(imageOptions[index].src);
		}
		return false;
	};

	return (
		<Box className={classes.container}>
			{textOptions &&
				textOptions.map((text: string, index: number) => (
					<Box className={classes.box} key={'multiTextOption' + index}>
						{isChecked(index, OPTION_TYPE.TEXT) ? (
							<img
								className={`${classes.textSelectBox} ${classes.checkbox}`}
								src={`${process.env.PUBLIC_URL}/img/checkbox-checked.png`}
								alt='Checked Checkbox'
							/>
						) : (
							<img
								className={`${classes.textSelectBox} ${classes.checkbox}`}
								src={`${process.env.PUBLIC_URL}/img/checkbox-unchecked.png`}
								alt='Unchecked Checkbox'
							/>
						)}
						<Typography className={classes.text}>{text}</Typography>
					</Box>
				))}
			{imageOptions &&
				imageOptions.map((image: Image, index: number) => (
					<Box className={classes.box} key={'multiImageOption' + index}>
						{isChecked(index, OPTION_TYPE.IMAGE) ? (
							<img
								className={classes.checkbox}
								src={`${process.env.PUBLIC_URL}/img/checkbox-checked.png`}
								alt='Checkbox'
							/>
						) : (
							<img
								className={classes.checkbox}
								src={`${process.env.PUBLIC_URL}/img/checkbox-unchecked.png`}
								alt='Checkbox'
							/>
						)}
						<img className={classes.image} src={image.src} alt={image.alt} />
					</Box>
				))}
		</Box>
	);
});
